import React, { useState, useRef } from 'react';
import { Upload, Download, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Toaster } from '../ui/sonner';
import { toast } from 'sonner';
import StepIndicator from './StepIndicator';
import Step1BasicInfo from './Step1BasicInfo';
import Step2MediaStyle from './Step2MediaStyle';
import Step3SocialLinks from './Step3SocialLinks';
import Step4CustomLinks from './Step4CustomLinks';
import SignaturePreview from './SignaturePreview';
import { defaultSignatureData } from '../../data/mockData';
import { copyAsRichText, copyHTMLSource, exportConfig, importConfig } from '../../utils/signatureUtils';

const SignatureGenerator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(defaultSignatureData);
  const importInputRef = useRef(null);

  const totalSteps = 4;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDataChange = (newData) => {
    // Debug: trace data updates
    try {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('handleDataChange called', {
          logoPreview: typeof newData === 'function' ? 'function' : newData?.logoPreview,
          bannerPreview: typeof newData === 'function' ? 'function' : newData?.bannerPreview,
        });
      }
    } catch (e) {}

    // Accept either a function updater or a partial object and merge into state to avoid
    // overwriting unrelated fields when children send partial updates.
    if (typeof newData === 'function') {
      setData((prev) => newData(prev));
    } else {
      setData((prev) => ({ ...prev, ...newData }));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const config = await importConfig(file);
        setData({ ...defaultSignatureData, ...config });
        toast.success('Configuração importada com sucesso!');
      } catch (err) {
        toast.error(err.message);
      }
    }
    e.target.value = '';
  };

  const handleExport = () => {
    exportConfig(data);
    toast.success('Configuração exportada com sucesso!');
  };

  const handleCopyFormatted = async () => {
    const success = await copyAsRichText(data);
    if (success) {
      toast.success('Assinatura copiada! Cole no seu cliente de e-mail.');
    } else {
      toast.error('Erro ao copiar. Tente novamente.');
    }
  };

  const handleCopyHTML = async () => {
    const success = await copyHTMLSource(data);
    if (success) {
      toast.success('Código HTML copiado!');
    } else {
      toast.error('Erro ao copiar. Tente novamente.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BasicInfo data={data} onChange={handleDataChange} />;
      case 2:
        return <Step2MediaStyle data={data} onChange={handleDataChange} />;
      case 3:
        return <Step3SocialLinks data={data} onChange={handleDataChange} />;
      case 4:
        return <Step4CustomLinks data={data} onChange={handleDataChange} />;
      default:
        return null;
    }
  };

  // Data with onChange for preview
  const previewData = {
    ...data,
    onChange: handleDataChange,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Gerador de Assinatura de E-mail</h1>
              <p className="text-slate-500 text-sm">Crie sua assinatura de e-mail facilmente</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Column */}
          <Card className="shadow-lg border-slate-200/80">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Informações Básicas
                </CardTitle>
                <div className="flex gap-2">
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => importInputRef.current.click()}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Importar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Exportar
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-6">
              <StepIndicator currentStep={currentStep} />
              
              <div className="min-h-[400px]">
                {renderStep()}
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                
                {currentStep < totalSteps ? (
                  <Button
                    onClick={handleNext}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600"
                  >
                    Próximo
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleCopyFormatted}
                    className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600"
                  >
                    Finalizar e Copiar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview Column */}
          <Card className="shadow-lg border-slate-200/80 lg:sticky lg:top-8 h-fit">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-lg font-semibold text-slate-800">Preview</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <SignaturePreview
                data={previewData}
                onCopyFormatted={handleCopyFormatted}
                onCopyHTML={handleCopyHTML}
              />
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer 
      <footer className="mt-12 py-6 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          <p>Gerador de Assinatura de E-mail • Crie assinaturas profissionais em minutos</p>
        </div>
      </footer>
      */}
    </div>
  );
};

export default SignatureGenerator;
